import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut, Package, ShoppingBag, MessageSquare, BarChart3, Plus, Trash2, Save, Search,
  Edit2, EyeOff, Eye, X, AlertTriangle, Upload, FileText, ImagePlus, Video, Loader2,
  Download, MessageCircle, Phone, Tag, Layers, GripVertical, Truck
} from 'lucide-react';
import {
  adminLogin, adminLogout, getAdminToken, setAdminToken,
  adminFetchProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, adminBulkDeleteProducts, adminBulkStock, adminBulkUpload,
  adminUploadFile,
  adminFetchOrders, adminUpdateOrderStatus, adminUpdateOrderTracking, adminBulkOrderStatus, adminFetchStats,
  adminFetchReviews, adminToggleHideReview, adminDeleteReview,
  adminFetchCoupons, adminCreateCoupon, adminUpdateCoupon, adminDeleteCoupon,
  adminCleanupTestOrders,
  adminFetchTaxonomies, adminCreateTaxonomy, adminUpdateTaxonomy, adminDeleteTaxonomy, adminReorderTaxonomies,
  adminChatConversations, adminChatUnreadTotal, adminChatThread, adminChatReply
} from '../api/admin';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import InstallAppHint from '../components/InstallAppHint';
import PushToggle from '../components/PushToggle';
import { ConfirmDialogHost, confirmDialog } from '../components/confirm-dialog';
import { toast } from '../hooks/use-toast';
import { sortSizes } from '../lib/sizes';
import CareInstructionsEditor from '../components/CareInstructionsEditor';

const LOGO_URL = 'https://customer-assets-lxgj4vgw.emergentagent.net/job_feron-collection/artifacts/41yxnxq7_Untitled%20design.webp';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convert an upload URL (returned as /api/uploads/xyz) to absolute so it works everywhere
const absMediaUrl = (u) => {
  if (!u) return u;
  if (u.startsWith('http')) return u;
  return `${BACKEND_URL}${u}`;
};

// -------- Login gate --------
const LoginGate = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await adminLogin(password);
      setAdminToken(token);
      onLogin(token);
    } catch (err) {
      toast({ title: 'Wrong password', description: 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(57,255,20,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(57,255,20,0.3) 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }} />
      <form onSubmit={submit} className="relative z-10 bg-gray-900 border border-[#22c55e]/30 rounded-lg p-8 w-full max-w-md shadow-2xl">
        <div className="flex justify-center mb-4">
          <img src={LOGO_URL} alt="Feron" className="h-24 w-auto object-contain drop-shadow-[0_0_20px_rgba(57,255,20,0.7)]" />
        </div>
        <h1 className="text-white text-2xl font-black text-center tracking-wider">OWNER PORTAL</h1>
        <p className="text-gray-400 text-xs text-center mt-1 tracking-widest">RESTRICTED ACCESS</p>

        <label className="block mt-6 text-[10px] font-bold tracking-widest text-[#22c55e]">PASSWORD</label>
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 bg-black border-gray-700 text-white focus:border-[#22c55e]"
          autoFocus
        />
        <Button type="submit" disabled={loading || !password}
          className="w-full mt-5 bg-[#22c55e] hover:bg-[#16a34a] text-black font-black tracking-widest h-11">
          {loading ? 'AUTHENTICATING...' : 'ENTER PORTAL'}
        </Button>
      </form>
    </div>
  );
};

// -------- Products tab --------
const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // product being edited
  const [creating, setCreating] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminFetchProducts(q);
      setProducts(data.items || []);
      setSelected(new Set());
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const onDelete = async (id) => {
    const ok = await confirmDialog({
      title: 'Delete this product?',
      description: 'This cannot be undone.',
      actionLabel: 'Delete',
      tone: 'destructive',
      testId: 'confirm-delete-product',
    });
    if (!ok) return;
    await adminDeleteProduct(id);
    toast({ title: 'Product deleted' });
    load();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === products.length && products.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(products.map((p) => p.id)));
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ok = await confirmDialog({
      title: `Delete ${selected.size} product${selected.size > 1 ? 's' : ''}?`,
      description: 'This cannot be undone.',
      actionLabel: 'Delete all',
      tone: 'destructive',
      testId: 'confirm-bulk-delete-products',
    });
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const res = await adminBulkDeleteProducts(Array.from(selected));
      toast({ title: `${res.deleted} product${res.deleted !== 1 ? 's' : ''} deleted` });
      load();
    } catch (err) {
      toast({ title: 'Bulk delete failed', description: err.response?.data?.detail || err.message });
    } finally { setBulkDeleting(false); }
  };

  const bulkStock = async (patch, label) => {
    if (selected.size === 0) return;
    setBulkDeleting(true);
    try {
      const res = await adminBulkStock(Array.from(selected), patch);
      toast({ title: `${res.modified} product${res.modified !== 1 ? 's' : ''} · ${label}` });
      load();
    } catch (err) {
      toast({ title: 'Bulk update failed', description: err.response?.data?.detail || err.message });
    } finally { setBulkDeleting(false); }
  };

  const promptStock = () => {
    const val = window.prompt(`Set stock quantity for ${selected.size} product${selected.size > 1 ? 's' : ''} (enter a number):`);
    if (val === null) return;
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0) { toast({ title: 'Invalid stock number' }); return; }
    bulkStock({ stock: n }, `stock set to ${n}`);
  };

  const allChecked = products.length > 0 && selected.size === products.length;
  const someChecked = selected.size > 0 && selected.size < products.length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <form onSubmit={onSearch} className="flex items-center gap-2 flex-1 max-w-md">
          <div className="flex items-center flex-1 border rounded px-2">
            <Search size={16} className="text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, type, collection..."
              className="flex-1 outline-none text-sm px-2 py-2" />
          </div>
          <Button type="submit" variant="outline">Search</Button>
        </form>
        <div className="flex gap-2">
          <Button onClick={() => setBulkOpen(true)} variant="outline" className="font-bold tracking-wider">
            <Upload size={16} className="mr-1" /> BULK CSV
          </Button>
          <Button onClick={() => setCreating(true)} className="bg-[#22c55e] hover:bg-[#16a34a] font-bold tracking-wider">
            <Plus size={16} className="mr-1" /> NEW PRODUCT
          </Button>
        </div>
      </div>

      {selected.size > 0 && (
        <div
          data-testid="products-bulk-bar"
          className="mb-3 flex flex-wrap items-center justify-between gap-3 border-2 border-[#22c55e] bg-green-50 rounded px-4 py-2.5"
        >
          <div className="text-sm font-semibold">
            {selected.size} product{selected.size > 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => bulkStock({ in_stock: true }, 'marked IN STOCK')}
              disabled={bulkDeleting}
              variant="outline"
              className="font-bold tracking-wider text-xs h-9 border-green-600 text-green-700 hover:bg-green-100"
              data-testid="products-bulk-instock"
            >
              MARK IN STOCK
            </Button>
            <Button
              onClick={() => bulkStock({ in_stock: false }, 'marked OUT OF STOCK')}
              disabled={bulkDeleting}
              variant="outline"
              className="font-bold tracking-wider text-xs h-9 border-red-500 text-red-600 hover:bg-red-50"
              data-testid="products-bulk-outofstock"
            >
              MARK OUT OF STOCK
            </Button>
            <Button
              onClick={promptStock}
              disabled={bulkDeleting}
              variant="outline"
              className="font-bold tracking-wider text-xs h-9"
              data-testid="products-bulk-setstock"
            >
              SET STOCK QTY
            </Button>
            <Button
              onClick={() => setSelected(new Set())}
              variant="outline"
              className="font-bold tracking-wider text-xs h-9"
              data-testid="products-bulk-clear"
            >
              CLEAR
            </Button>
            <Button
              onClick={bulkDelete}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700 text-white font-bold tracking-wider text-xs h-9"
              data-testid="products-bulk-delete"
            >
              {bulkDeleting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
              DELETE SELECTED
            </Button>
          </div>
        </div>
      )}

      {loading ? <div className="text-center py-10 text-gray-500">Loading...</div> : (
        <div className="border rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-3 w-10">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked; }}
                    onChange={selectAll}
                    className="accent-[#22c55e] w-4 h-4"
                    data-testid="products-select-all"
                  />
                </th>
                <th className="text-left p-3 font-semibold">ID</th>
                <th className="text-left p-3 font-semibold">Product</th>
                <th className="text-left p-3 font-semibold">Type</th>
                <th className="text-right p-3 font-semibold">Price (₹)</th>
                <th className="text-right p-3 font-semibold">Stock</th>
                <th className="text-center p-3 font-semibold">In Stock</th>
                <th className="text-right p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const checked = selected.has(p.id);
                return (
                  <tr key={p.id} className={`border-b hover:bg-gray-50 ${checked ? 'bg-green-50/60' : ''}`}>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelect(p.id)}
                        className="accent-[#22c55e] w-4 h-4"
                        data-testid={`products-select-${p.id}`}
                      />
                    </td>
                    <td className="p-3 text-gray-500 font-mono">#{p.id}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <img src={p.image} alt="" className="w-10 h-12 object-cover rounded" />
                        <div>
                          <div className="font-semibold line-clamp-1 max-w-xs">{p.name}</div>
                          <div className="text-xs text-gray-500">{p.collection}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">{p.type}</td>
                    <td className="p-3 text-right font-semibold">₹{p.price}</td>
                    <td className="p-3 text-right">
                      <span className={p.stock !== undefined && p.stock <= 10 ? 'text-red-600 font-semibold' : ''}>
                        {p.stock ?? '—'}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${p.inStock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {p.inStock ? 'YES' : 'NO'}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => setEditing(p)} className="text-gray-600 hover:text-[#22c55e] p-1"><Edit2 size={16} /></button>
                      <button onClick={() => onDelete(p.id)} className="text-gray-600 hover:text-red-600 p-1 ml-1" data-testid={`delete-product-${p.id}`}><Trash2 size={16} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && <ProductEditor product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {creating && <ProductEditor product={null} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {bulkOpen && <BulkUploadModal onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
    </div>
  );
};

// -------- Bulk CSV upload modal --------
const CSV_TEMPLATE = `name,type,gender,price,mrp,discount,image,collection,description,stock,inStock
Sample Hoodie,Hoodies,men,1499,2499,40,https://images.unsplash.com/photo-1564557287817-3785e38ec1f5?w=600,Feron Originals,Cozy pullover hoodie,50,true
Sample Tee,Oversized T-Shirts,women,799,1299,38,https://images.unsplash.com/photo-1661110546797-d86cc72a2765?w=600,Feron Originals,Oversized fit tee,80,true`;

const BulkUploadModal = ({ onClose, onDone }) => {
  const [csvText, setCsvText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setCsvText(text);
  };

  const submit = async () => {
    if (!csvText.trim()) { toast({ title: 'Please paste CSV or upload a file' }); return; }
    setUploading(true);
    try {
      const res = await adminBulkUpload(csvText);
      setResult(res);
      toast({ title: `Uploaded: ${res.created} products`, description: res.errors.length ? `${res.errors.length} rows failed` : 'All rows imported' });
    } catch (err) {
      toast({ title: 'Bulk upload failed', description: err.response?.data?.detail || err.message });
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="font-black text-lg flex items-center gap-2"><Upload size={18} /> BULK CSV UPLOAD</h3>
          <button onClick={onClose}><X /></button>
        </div>
        <div className="p-5 space-y-4">
          {!result ? (
            <>
              <div className="text-sm text-gray-700">
                Upload a CSV file or paste CSV text. Required columns: <strong>name, type, price, mrp, image</strong>.
                Optional: gender, discount, collection, description, stock, inStock.
              </div>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 border rounded px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                  <FileText size={14} /> Upload .csv file
                  <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
                </label>
                <button onClick={() => setCsvText(CSV_TEMPLATE)} className="text-xs font-bold text-[#22c55e] hover:underline">
                  Load example template
                </button>
              </div>
              <textarea
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder="Paste CSV text here..."
                rows={12}
                className="w-full border rounded px-3 py-2 text-xs font-mono outline-none focus:border-[#22c55e]"
              />
            </>
          ) : (
            <div className="space-y-3">
              <div className="text-green-700 font-bold text-lg">✓ Imported {result.created} products</div>
              {result.errors.length > 0 && (
                <div>
                  <div className="text-red-600 font-semibold text-sm">{result.errors.length} row(s) failed:</div>
                  <ul className="mt-2 text-xs text-gray-700 space-y-1 max-h-40 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <li key={`${e.row}-${i}`}>Row {e.row}: {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="border-t p-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && (
            <Button onClick={submit} disabled={uploading || !csvText.trim()} className="bg-[#22c55e] hover:bg-[#16a34a] font-bold">
              {uploading ? 'UPLOADING...' : 'IMPORT PRODUCTS'}
            </Button>
          )}
          {result && (
            <Button onClick={onDone} className="bg-[#22c55e] hover:bg-[#16a34a] font-bold">DONE</Button>
          )}
        </div>
      </div>
    </div>
  );
};

// -------- Product editor (create + edit) with multi-image + multi-video upload --------
const MediaThumb = ({ url, kind, onRemove, onSetCover, isCover }) => (
  <div className={`relative w-24 h-28 rounded overflow-hidden border-2 ${isCover ? 'border-[#22c55e]' : 'border-gray-200'} bg-gray-100 shrink-0 group`}>
    {kind === 'video' ? (
      <video src={absMediaUrl(url)} className="w-full h-full object-cover" muted playsInline />
    ) : (
      <img src={absMediaUrl(url)} alt="" className="w-full h-full object-cover" />
    )}
    {kind === 'video' && (
      <div className="absolute bottom-1 left-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded flex items-center gap-1">
        <Video size={10} /> VIDEO
      </div>
    )}
    {isCover && <div className="absolute top-1 left-1 bg-[#22c55e] text-black text-[9px] font-bold px-1.5 py-0.5 rounded">COVER</div>}
    <button type="button" onClick={onRemove}
      className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition">
      <X size={12} />
    </button>
    {onSetCover && !isCover && kind === 'image' && (
      <button type="button" onClick={onSetCover}
        className="absolute bottom-1 right-1 bg-white/95 text-black text-[9px] font-bold px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition">
        SET COVER
      </button>
    )}
  </div>
);

const ProductEditor = ({ product, onClose, onSaved }) => {
  const isNew = !product;
  const [types, setTypes] = useState([]);
  const [collectionOptions, setCollectionOptions] = useState([]);
  useEffect(() => {
    adminFetchTaxonomies('type').then((d) => setTypes(d.items || [])).catch(() => {});
    adminFetchTaxonomies('collection').then((d) => setCollectionOptions(d.items || [])).catch(() => {});
  }, []);
  const [form, setForm] = useState(product || {
    name: '', type: 'Oversized T-Shirts', gender: 'men',
    mrp: 999, price: 799, discount: 20,
    priceEUR: null, mrpEUR: null, discountEUR: null,
    image: '',
    images: [],
    videos: [],
    collection: 'Feron Originals', description: '', stock: 100, inStock: true
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ image: false, video: false });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Auto-calc discount% from INR mrp + price
  const updateInrPricing = (patch) => {
    setForm((f) => {
      const next = { ...f, ...patch };
      const mrp = Number(next.mrp) || 0;
      const price = Number(next.price) || 0;
      if (mrp > 0 && price >= 0 && price <= mrp) {
        next.discount = Math.round(((mrp - price) / mrp) * 100);
      }
      return next;
    });
  };

  // Auto-calc discountEUR% from EUR mrp + price (mirrors INR behaviour)
  const updateEurPricing = (patch) => {
    setForm((f) => {
      const next = { ...f, ...patch };
      const mrpE = Number(next.mrpEUR);
      const priceE = Number(next.priceEUR);
      if (mrpE > 0 && priceE >= 0 && priceE <= mrpE) {
        next.discountEUR = Math.round(((mrpE - priceE) / mrpE) * 100);
      } else if (!next.mrpEUR || !next.priceEUR) {
        next.discountEUR = null;
      }
      return next;
    });
  };

  // Live-computed savings for the top summary
  const savingsINR = Math.max(0, (Number(form.mrp) || 0) - (Number(form.price) || 0));
  const savingsEUR = form.mrpEUR && form.priceEUR ? Math.max(0, Number(form.mrpEUR) - Number(form.priceEUR)) : 0;
  const displayDiscountEUR = form.discountEUR ?? (form.mrpEUR && form.priceEUR && form.mrpEUR > 0 ? Math.round(((form.mrpEUR - form.priceEUR) / form.mrpEUR) * 100) : null);

  const onUpload = async (files, kind) => {
    if (!files || files.length === 0) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    const uploadedUrls = [];
    for (const f of files) {
      try {
        const res = await adminUploadFile(f);
        uploadedUrls.push(res.url);
      } catch (err) {
        toast({ title: 'Upload failed', description: err.response?.data?.detail || err.message });
      }
    }
    if (uploadedUrls.length > 0) {
      setForm((f) => {
        const next = { ...f };
        if (kind === 'image') {
          const imgs = [...(f.images || []), ...uploadedUrls];
          next.images = imgs;
          if (!f.image && imgs.length > 0) next.image = imgs[0];
        } else {
          next.videos = [...(f.videos || []), ...uploadedUrls];
        }
        return next;
      });
    }
    setUploading((u) => ({ ...u, [kind]: false }));
  };

  const removeImage = (idx) => {
    setForm((f) => {
      const imgs = [...(f.images || [])];
      const removed = imgs.splice(idx, 1)[0];
      const next = { ...f, images: imgs };
      if (removed === f.image) next.image = imgs[0] || '';
      return next;
    });
  };
  const removeVideo = (idx) => {
    setForm((f) => {
      const vids = [...(f.videos || [])];
      vids.splice(idx, 1);
      return { ...f, videos: vids };
    });
  };
  const setCover = (url) => setField('image', url);

  const save = async () => {
    if (!form.name || !form.type) { toast({ title: 'Name and type are required' }); return; }
    if (!form.image && (!form.images || form.images.length === 0)) {
      toast({ title: 'Add at least one image' }); return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.__newSize;
      delete payload.__newColor;
      if (!payload.image) payload.image = payload.images[0];
      if (isNew) {
        await adminCreateProduct(payload);
        toast({ title: 'Product created' });
      } else {
        await adminUpdateProduct(product.id, payload);
        toast({ title: 'Product updated' });
      }
      onSaved();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const currentCover = form.image;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4 sticky top-0 bg-white z-10">
          <h3 className="font-black text-lg">{isNew ? 'NEW PRODUCT' : `EDIT #${product.id}`}</h3>
          <button onClick={onClose}><X /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* Media */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-black tracking-wider text-gray-800">PRODUCT IMAGES</label>
              <label className={`inline-flex items-center gap-2 border-2 border-dashed border-gray-300 hover:border-[#22c55e] rounded px-3 py-2 text-xs cursor-pointer font-bold ${uploading.image ? 'opacity-60' : ''}`}>
                {uploading.image ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                UPLOAD IMAGES
                <input type="file" accept="image/*" multiple className="hidden"
                  onChange={(e) => { onUpload(Array.from(e.target.files), 'image'); e.target.value = ''; }} />
              </label>
            </div>
            <div className="flex gap-2 flex-wrap p-3 bg-gray-50 rounded min-h-[110px]">
              {(form.images || []).length === 0 && <p className="text-xs text-gray-500 self-center">No images yet. Upload one or more.</p>}
              {(form.images || []).map((u, i) => (
                <MediaThumb key={`${u}-${i}`} url={u} kind="image"
                  isCover={u === currentCover}
                  onRemove={() => removeImage(i)}
                  onSetCover={() => setCover(u)} />
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-black tracking-wider text-gray-800">PRODUCT VIDEOS (auto-plays in store)</label>
              <label className={`inline-flex items-center gap-2 border-2 border-dashed border-gray-300 hover:border-[#22c55e] rounded px-3 py-2 text-xs cursor-pointer font-bold ${uploading.video ? 'opacity-60' : ''}`}>
                {uploading.video ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} />}
                UPLOAD VIDEOS
                <input type="file" accept="video/*" multiple className="hidden"
                  onChange={(e) => { onUpload(Array.from(e.target.files), 'video'); e.target.value = ''; }} />
              </label>
            </div>
            <div className="flex gap-2 flex-wrap p-3 bg-gray-50 rounded min-h-[110px]">
              {(form.videos || []).length === 0 && <p className="text-xs text-gray-500 self-center">No videos yet. Upload short reels (max 25MB each).</p>}
              {(form.videos || []).map((u, i) => (
                <MediaThumb key={`${u}-${i}`} url={u} kind="video" onRemove={() => removeVideo(i)} />
              ))}
            </div>
          </div>

          {/* Live pricing summary — auto-updates as MRP/Price are typed */}
          <div className="mb-4 rounded-lg border-2 border-[#22c55e]/40 bg-gradient-to-r from-[#22c55e]/10 to-white p-4" data-testid="pricing-summary">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Live pricing summary</div>
              <div className="flex items-center gap-4 flex-wrap">
                {/* INR */}
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wider text-gray-500">₹</span>
                  <span className="text-sm text-gray-400 line-through">₹{Number(form.mrp) || 0}</span>
                  <span className="text-base font-black text-gray-900">₹{Number(form.price) || 0}</span>
                  <span className="text-xs font-bold px-2 py-0.5 bg-[#22c55e] text-black rounded" data-testid="summary-discount-inr">{Number(form.discount) || 0}% OFF</span>
                  {savingsINR > 0 && <span className="text-xs text-gray-600">save ₹{savingsINR}</span>}
                </div>
                <div className="w-px h-6 bg-gray-300"></div>
                {/* EUR */}
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wider text-gray-500">€</span>
                  {form.mrpEUR && form.priceEUR ? (
                    <>
                      <span className="text-sm text-gray-400 line-through">€{Number(form.mrpEUR).toFixed(2)}</span>
                      <span className="text-base font-black text-gray-900">€{Number(form.priceEUR).toFixed(2)}</span>
                      <span className="text-xs font-bold px-2 py-0.5 bg-[#22c55e] text-black rounded" data-testid="summary-discount-eur">{displayDiscountEUR ?? 0}% OFF</span>
                      {savingsEUR > 0 && <span className="text-xs text-gray-600">save €{savingsEUR.toFixed(2)}</span>}
                    </>
                  ) : (
                    <span className="text-xs text-gray-500 italic">EUR blank — will auto-convert from ₹ on the storefront</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Fields */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-700">NAME</label>
              <Input value={form.name} onChange={(e) => setField('name', e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700">TYPE</label>
              <select value={form.type} onChange={(e) => setField('type', e.target.value)} className="w-full border rounded px-3 py-2 text-sm" data-testid="product-type-select">
                {(types.length > 0 ? types.map((t) => t.label) : ['Oversized T-Shirts','Hoodies','Sweatshirts','Joggers','Shirts','Backpacks','Caps','Sneakers','Jackets','Dresses']).map((t) => <option key={t} value={t}>{t}</option>)}
                {/* Ensure current value stays selectable even if not in list */}
                {form.type && !(types.length > 0 ? types.map((t) => t.label) : ['Oversized T-Shirts','Hoodies','Sweatshirts','Joggers','Shirts','Backpacks','Caps','Sneakers','Jackets','Dresses']).includes(form.type) && (
                  <option value={form.type}>{form.type} (custom)</option>
                )}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700">GENDER</label>
              <select value={form.gender} onChange={(e) => setField('gender', e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
                <option value="men">Men</option>
                <option value="women">Women</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700">COLLECTION</label>
              <Input list="collection-options" value={form.collection} onChange={(e) => setField('collection', e.target.value)} data-testid="product-collection-input" />
              <datalist id="collection-options">
                {collectionOptions.map((c) => <option key={c.id} value={c.label} />)}
              </datalist>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700">MRP (₹)</label>
              <Input type="number" value={form.mrp} onChange={(e) => updateInrPricing({ mrp: +e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700">PRICE (₹)</label>
              <Input type="number" value={form.price} onChange={(e) => updateInrPricing({ price: +e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700">DISCOUNT (%) <span className="text-[10px] font-normal text-[#22c55e]">· auto</span></label>
              <Input type="number" value={form.discount} onChange={(e) => setField('discount', +e.target.value)} />
              <p className="text-[10px] text-gray-500 mt-1">Auto-calculated from ₹ MRP and Price. You can override manually.</p>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700">MRP (€) <span className="text-[10px] font-normal text-gray-400">· optional</span></label>
              <Input type="number" step="0.01" placeholder="e.g. 24.99"
                value={form.mrpEUR ?? ''}
                data-testid="product-mrp-eur"
                onChange={(e) => updateEurPricing({ mrpEUR: e.target.value === '' ? null : Number(e.target.value) })} />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700">PRICE (€) <span className="text-[10px] font-normal text-gray-400">· optional</span></label>
              <Input type="number" step="0.01" placeholder="e.g. 14.99"
                value={form.priceEUR ?? ''}
                data-testid="product-price-eur"
                onChange={(e) => updateEurPricing({ priceEUR: e.target.value === '' ? null : Number(e.target.value) })} />
              <p className="text-[10px] text-gray-500 mt-1">Leave blank to auto-convert from ₹ using the exchange rate.</p>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700">DISCOUNT (€ %) <span className="text-[10px] font-normal text-[#22c55e]">· auto</span></label>
              <Input type="number" value={form.discountEUR ?? ''} placeholder="—"
                data-testid="product-discount-eur"
                onChange={(e) => setField('discountEUR', e.target.value === '' ? null : +e.target.value)} />
              <p className="text-[10px] text-gray-500 mt-1">Auto-calculated from € MRP and Price. You can override manually.</p>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700">STOCK</label>
              <Input type="number" value={form.stock ?? 0} onChange={(e) => setField('stock', +e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700">IN STOCK</label>
              <div className="flex items-center gap-2 h-10">
                <input type="checkbox" checked={!!form.inStock} onChange={(e) => setField('inStock', e.target.checked)} className="accent-[#22c55e] w-5 h-5" />
                <span className="text-sm">{form.inStock ? 'Available for sale' : 'Hidden from shop'}</span>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-700">AVAILABLE SIZES</label>
              <p className="text-[10px] text-gray-500 mb-2">Toggle the sizes shoppers can pick. Add a custom size (e.g. UK 8, 32, EU 42) using the input below.</p>
              <div className="flex flex-wrap gap-2" data-testid="sizes-editor">
                {sortSizes(['XXS','XS','S','M','L','XL','XXL','3XL','4XL','28','30','32','34','36','38','40','42','UK 6','UK 7','UK 8','UK 9','UK 10','UK 11']
                  .concat((form.sizes || []).filter((s) => !['XXS','XS','S','M','L','XL','XXL','3XL','4XL','28','30','32','34','36','38','40','42','UK 6','UK 7','UK 8','UK 9','UK 10','UK 11'].includes(s))))
                  .map((s) => {
                    const active = (form.sizes || []).includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        data-testid={`size-chip-${s}`}
                        onClick={() => setField('sizes', active ? (form.sizes || []).filter((x) => x !== s) : sortSizes([...(form.sizes || []), s]))}
                        className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold tracking-wider transition ${active ? 'bg-[#22c55e] border-[#22c55e] text-black' : 'bg-white border-gray-300 text-gray-600 hover:border-[#22c55e]'}`}
                      >
                        {s}
                      </button>
                    );
                  })}
              </div>
              <div className="mt-2 flex gap-2 items-center">
                <Input
                  placeholder="Add custom size (e.g. UK 12, EU 44, Free Size)"
                  value={form.__newSize || ''}
                  onChange={(e) => setField('__newSize', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = (form.__newSize || '').trim();
                      if (val && !(form.sizes || []).includes(val)) {
                        setForm((f) => ({ ...f, sizes: sortSizes([...(f.sizes || []), val]), __newSize: '' }));
                      }
                    }
                  }}
                  className="h-9"
                  data-testid="size-add-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const val = (form.__newSize || '').trim();
                    if (val && !(form.sizes || []).includes(val)) {
                      setForm((f) => ({ ...f, sizes: sortSizes([...(f.sizes || []), val]), __newSize: '' }));
                    }
                  }}
                  className="h-9 font-bold text-xs whitespace-nowrap"
                  data-testid="size-add-btn"
                >
                  + ADD
                </Button>
              </div>
              {(form.sizes || []).length === 0 && (
                <p className="text-[11px] text-red-600 mt-2">⚠ No sizes selected — shoppers won't be able to add this to their cart.</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-700">AVAILABLE COLOURS</label>
              <p className="text-[10px] text-gray-500 mb-2">Toggle preset colours or add a custom one (any CSS colour name or hex, e.g. "Sage Green", "#f97316").</p>
              <div className="flex flex-wrap gap-2" data-testid="colors-editor">
                {['Black','White','Navy','Olive','Grey','Red','Blue','Green','Yellow','Pink','Purple','Beige','Brown','Maroon']
                  .concat((form.colors || []).filter((c) => !['Black','White','Navy','Olive','Grey','Red','Blue','Green','Yellow','Pink','Purple','Beige','Brown','Maroon'].includes(c)))
                  .map((c) => {
                    const active = (form.colors || []).includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        data-testid={`color-chip-${c}`}
                        onClick={() => setField('colors', active ? (form.colors || []).filter((x) => x !== c) : [...(form.colors || []), c])}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-bold tracking-wider transition ${active ? 'bg-[#22c55e] border-[#22c55e] text-black' : 'bg-white border-gray-300 text-gray-600 hover:border-[#22c55e]'}`}
                      >
                        <span
                          className="inline-block w-3.5 h-3.5 rounded-full border border-gray-300 shrink-0"
                          style={{ background: c.startsWith('#') ? c : c.toLowerCase() }}
                        />
                        {c}
                      </button>
                    );
                  })}
              </div>
              <div className="mt-2 flex gap-2 items-center">
                <Input
                  placeholder='Add custom colour (e.g. "Sage Green" or "#f97316")'
                  value={form.__newColor || ''}
                  onChange={(e) => setField('__newColor', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = (form.__newColor || '').trim();
                      if (val && !(form.colors || []).includes(val)) {
                        setForm((f) => ({ ...f, colors: [...(f.colors || []), val], __newColor: '' }));
                      }
                    }
                  }}
                  className="h-9"
                  data-testid="color-add-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const val = (form.__newColor || '').trim();
                    if (val && !(form.colors || []).includes(val)) {
                      setForm((f) => ({ ...f, colors: [...(f.colors || []), val], __newColor: '' }));
                    }
                  }}
                  className="h-9 font-bold text-xs whitespace-nowrap"
                  data-testid="color-add-btn"
                >
                  + ADD
                </Button>
              </div>
            </div>
            <div className="md:col-span-2">
              <CareInstructionsEditor
                value={form.careInstructions}
                onChange={(v) => setField('careInstructions', v)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-700">DESCRIPTION</label>
              <textarea value={form.description || ''} onChange={(e) => setField('description', e.target.value)} rows={3} className="w-full border rounded px-3 py-2 text-sm outline-none" />
            </div>
          </div>
        </div>

        <div className="border-t p-4 flex justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#22c55e] hover:bg-[#16a34a] font-bold">
            <Save size={14} className="mr-1" /> {saving ? 'SAVING...' : 'SAVE'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// -------- Orders tab --------
// -------- Order details modal --------
// Extract a phone number from address free-text if `phone` isn't a raw number.
const extractPhone = (addr) => {
  if (!addr) return '';
  const candidates = [addr.phone, addr.line1, addr.line2, addr.city].filter(Boolean).join(' ');
  const m = candidates.match(/\+?\d[\d\s\-()]{7,}\d/);
  if (!m) return '';
  // Strip everything except digits and leading +
  return m[0].replace(/[^\d+]/g, '');
};

const TrackingEditor = ({ order, onSaved }) => {
  const [tracking, setTracking] = React.useState(order.tracking || {});
  const [saving, setSaving] = React.useState(false);
  const set = (k, v) => setTracking((t) => ({ ...t, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateOrderTracking(order.order_id, tracking);
      toast({ title: 'Tracking saved · customer can now see it on /track-order' });
      onSaved && onSaved();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message });
    } finally { setSaving(false); }
  };
  return (
    <div className="mt-4 border rounded-lg p-4 bg-neutral-50" data-testid="tracking-editor">
      <h4 className="font-bold text-sm tracking-wider mb-2 flex items-center gap-2">
        <Truck size={14} /> DELIVERY / TRACKING DETAILS
      </h4>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-bold text-gray-700">COURIER</label>
          <Input value={tracking.courier || ''} onChange={(e) => set('courier', e.target.value)} placeholder="e.g. DHL, BlueDart, DPD" data-testid="tracking-courier" />
        </div>
        <div>
          <label className="text-[11px] font-bold text-gray-700">TRACKING NUMBER</label>
          <Input value={tracking.tracking_number || ''} onChange={(e) => set('tracking_number', e.target.value)} placeholder="AWB / consignment #" data-testid="tracking-number" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] font-bold text-gray-700">TRACKING URL</label>
          <Input value={tracking.tracking_url || ''} onChange={(e) => set('tracking_url', e.target.value)} placeholder="https://courier.com/track?id=..." data-testid="tracking-url" />
        </div>
        <div>
          <label className="text-[11px] font-bold text-gray-700">ESTIMATED DELIVERY</label>
          <Input value={tracking.estimated_delivery || ''} onChange={(e) => set('estimated_delivery', e.target.value)} placeholder="e.g. 12 Feb, 2026" data-testid="tracking-eta" />
        </div>
        <div>
          <label className="text-[11px] font-bold text-gray-700">NOTES</label>
          <Input value={tracking.notes || ''} onChange={(e) => set('notes', e.target.value)} placeholder="Optional message to buyer" data-testid="tracking-notes" />
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="mt-3 bg-black hover:bg-neutral-800 text-white font-bold tracking-wider" data-testid="tracking-save-btn">
        {saving ? '...' : 'SAVE TRACKING'}
      </Button>
    </div>
  );
};

const OrderDetailsModal = ({ order, onClose }) => {
  if (!order) return null;
  const status = (order.status || 'placed').toLowerCase();
  const symbol = order.currency === 'EUR' ? '€' : '₹';
  const money = (n) => `${symbol}${Number(n || 0).toFixed(2)}`;
  const addr = order.address || {};
  const total = Number(order.total || 0);
  const vatRate = Number(order.vat_rate || 0);
  const vatAmount = Number(order.vat_amount || 0) || (vatRate > 0 ? total - total / (1 + vatRate / 100) : 0);
  const netTotal = total - vatAmount;

  const invoiceUrl = `${BACKEND_URL}/api/orders/${order.order_id}/invoice`;
  const shortId = order.order_id.slice(0, 8).toUpperCase();
  const phone = extractPhone(addr);
  const waBase = phone ? `https://wa.me/${phone.replace(/^\+/, '')}` : '';
  const invoiceMsg = encodeURIComponent(
    `Hi ${addr.name || ''}, thanks for your order at Feron Store!\n\n` +
    `Order: #${shortId}\nTotal: ${money(total)}\n\n` +
    `Download your invoice here: ${invoiceUrl}`
  );
  const contactMsg = encodeURIComponent(
    `Hi ${addr.name || ''}, this is Feron Store regarding your order #${shortId}. How can we help?`
  );
  const waInvoiceLink = waBase ? `${waBase}?text=${invoiceMsg}` : '';
  const waContactLink = waBase ? `${waBase}?text=${contactMsg}` : '';

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4 sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-black text-lg">ORDER #{shortId}</h3>
            <p className="text-xs text-gray-500">{new Date(order.created_at).toLocaleString()}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`inline-block px-3 py-1 rounded-full text-[11px] font-bold uppercase ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>
            <button onClick={onClose} data-testid="close-order-details"><X /></button>
          </div>
        </div>

        {/* Action bar: Invoice + WhatsApp */}
        <div className="px-5 pt-4 flex flex-wrap gap-2">
          <a
            href={invoiceUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="download-invoice-btn"
            className="inline-flex items-center gap-1.5 bg-[#22c55e] hover:bg-[#16a34a] text-black font-bold text-xs tracking-wider px-3 py-2 rounded transition"
          >
            <Download size={14} /> DOWNLOAD INVOICE
          </a>
          {waInvoiceLink ? (
            <a
              href={waInvoiceLink}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-invoice-btn"
              className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-xs tracking-wider px-3 py-2 rounded transition"
            >
              <MessageCircle size={14} /> SEND INVOICE ON WHATSAPP
            </a>
          ) : (
            <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-400 font-bold text-xs tracking-wider px-3 py-2 rounded cursor-not-allowed" title="No phone number found">
              <MessageCircle size={14} /> WHATSAPP (no phone)
            </span>
          )}
          {waContactLink && (
            <a
              href={waContactLink}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-contact-btn"
              className="inline-flex items-center gap-1.5 border-2 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-bold text-xs tracking-wider px-3 py-2 rounded transition"
            >
              <Phone size={14} /> CONTACT CUSTOMER
            </a>
          )}
        </div>

        <div className="p-5 grid md:grid-cols-2 gap-5">
          <div>
            <h4 className="font-bold text-sm tracking-wider mb-2">SHIPPING ADDRESS</h4>
            <div className="border rounded p-3 text-sm space-y-0.5">
              <div className="font-semibold">{addr.name}</div>
              <div className="text-gray-600">{addr.phone}</div>
              {addr.email && <div className="text-gray-600">{addr.email}</div>}
              <div className="mt-2">{addr.line1}</div>
              {addr.line2 && <div>{addr.line2}</div>}
              <div>{addr.city} {addr.postcode}</div>
              <div className="font-semibold">{addr.country}</div>
            </div>

            <h4 className="font-bold text-sm tracking-wider mt-4 mb-2">CUSTOMER</h4>
            <div className="border rounded p-3 text-sm">
              <div className="text-gray-500 text-xs">User ID</div>
              <div className="font-mono text-xs break-all">{order.user_id}</div>
              {phone && (
                <>
                  <div className="text-gray-500 text-xs mt-2">Detected phone</div>
                  <div className="font-mono text-xs">{phone}</div>
                </>
              )}
            </div>

            {(order.vat_country || vatRate > 0) && (
              <>
                <h4 className="font-bold text-sm tracking-wider mt-4 mb-2">TAX / VAT</h4>
                <div className="border rounded p-3 text-sm bg-green-50/40" data-testid="order-vat-block">
                  <div className="flex justify-between"><span className="text-gray-600">Country</span><span className="font-semibold">{order.vat_country || order.country_code || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">VAT Rate</span><span className="font-semibold">{vatRate.toFixed(1)}%</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">VAT Amount</span><span className="font-semibold">{money(vatAmount)}</span></div>
                  <div className="flex justify-between border-t pt-1 mt-1"><span className="text-gray-600">Net (excl. VAT)</span><span className="font-semibold">{money(netTotal)}</span></div>
                </div>
              </>
            )}
          </div>

          <div>
            <h4 className="font-bold text-sm tracking-wider mb-2">ITEMS ({order.items?.length || 0})</h4>
            <div className="space-y-2">
              {(order.items || []).map((it, i) => (
                <div key={`${it.productId || it.name || 'item'}-${i}`} className="border rounded p-2 flex gap-3">
                  {it.image && <img src={it.image} alt="" className="w-14 h-16 object-cover rounded shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm line-clamp-1">{it.name}</div>
                    <div className="text-xs text-gray-500">Size: {it.size} · Qty: {it.qty}</div>
                    <div className="font-bold text-sm mt-0.5">{money(it.price)} <span className="text-xs text-gray-400 font-normal">× {it.qty}</span></div>
                  </div>
                  <div className="text-sm font-bold self-center">{money((it.price || 0) * (it.qty || 1))}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 border-t pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{money(order.shipping)}</span></div>
              {order.discount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>- {money(order.discount)}</span></div>}
              {vatRate > 0 && (
                <div className="flex justify-between text-gray-500 text-xs"><span>incl. VAT ({vatRate.toFixed(1)}%)</span><span>{money(vatAmount)}</span></div>
              )}
              <div className="flex justify-between font-black text-base border-t pt-2 mt-2"><span>Total</span><span>{money(order.total)}</span></div>
            </div>
          </div>
        </div>
        <div className="px-5 pb-5">
          <TrackingEditor order={order} onSaved={onClose} />
        </div>
      </div>
    </div>
  );
};

const STATUS_STEPS = ['placed', 'packed', 'shipped', 'delivered'];
const STATUS_COLORS = {
  placed: 'bg-blue-100 text-blue-700',
  packed: 'bg-yellow-100 text-yellow-700',
  shipped: 'bg-purple-100 text-purple-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [detailsOrder, setDetailsOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminFetchOrders(q);
      setOrders(data.items || []);
      setSelected(new Set());
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const changeStatus = async (order_id, newStatus) => {
    try {
      await adminUpdateOrderStatus(order_id, newStatus);
      toast({ title: `Order marked ${newStatus}` });
      load();
    } catch (err) {
      toast({ title: 'Update failed', description: err.response?.data?.detail || err.message });
    }
  };

  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const bulkMark = async (status) => {
    if (selected.size === 0) return;
    const ok = await confirmDialog({
      title: `Mark ${selected.size} order(s) as ${status.toUpperCase()}?`,
      description: 'Customers may receive status update notifications.',
      actionLabel: `Mark as ${status}`,
      testId: 'confirm-bulk-mark-orders',
    });
    if (!ok) return;
    try {
      const res = await adminBulkOrderStatus(Array.from(selected), status);
      toast({ title: `Updated ${res.modified} order(s)`, description: `Marked as ${status}` });
      load();
    } catch (err) {
      toast({ title: 'Bulk update failed', description: err.response?.data?.detail || err.message });
    }
  };

  // Count per status for chip badges + filter list
  const statusCounts = orders.reduce((acc, o) => {
    const s = (o.status || 'placed').toLowerCase();
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const filtered = statusFilter === 'all' ? orders : orders.filter((o) => (o.status || 'placed').toLowerCase() === statusFilter);

  const selectAll = () => {
    if (selected.size === filtered.length && filtered.length > 0) setSelected(new Set());
    else setSelected(new Set(filtered.map((o) => o.order_id)));
  };

  const CHIPS = [
    { key: 'all', label: 'ALL', color: 'bg-black text-white' },
    { key: 'placed', label: 'PLACED', color: 'bg-blue-500 text-white' },
    { key: 'packed', label: 'PACKED', color: 'bg-yellow-500 text-black' },
    { key: 'shipped', label: 'SHIPPED', color: 'bg-purple-500 text-white' },
    { key: 'delivered', label: 'DELIVERED', color: 'bg-[#22c55e] text-black' },
    { key: 'cancelled', label: 'CANCELLED', color: 'bg-red-500 text-white' },
  ];

  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 mb-4 max-w-md">
        <div className="flex items-center flex-1 border rounded px-2">
          <Search size={16} className="text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by user, order id, name..." className="flex-1 outline-none text-sm px-2 py-2" />
        </div>
        <Button type="submit" variant="outline">Search</Button>
      </form>

      {/* Status filter chips */}
      <div className="flex gap-2 flex-wrap mb-4">
        {CHIPS.map((c) => {
          const count = c.key === 'all' ? orders.length : (statusCounts[c.key] || 0);
          const active = statusFilter === c.key;
          return (
            <button key={c.key} onClick={() => { setStatusFilter(c.key); setSelected(new Set()); }}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold tracking-wider transition border-2 ${active ? `${c.color} border-transparent shadow` : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`}>
              {c.label}
              <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] ${active ? 'bg-white/25' : 'bg-gray-100 text-gray-600'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {selected.size > 0 && (
        <div className="mb-3 p-3 bg-black text-white rounded flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm font-semibold">{selected.size} order{selected.size > 1 ? 's' : ''} selected</div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => bulkMark('packed')} className="px-3 py-1.5 rounded bg-yellow-500 text-black text-xs font-bold hover:bg-yellow-400">MARK PACKED</button>
            <button onClick={() => bulkMark('shipped')} className="px-3 py-1.5 rounded bg-purple-500 text-white text-xs font-bold hover:bg-purple-400">MARK SHIPPED</button>
            <button onClick={() => bulkMark('delivered')} className="px-3 py-1.5 rounded bg-[#22c55e] text-black text-xs font-bold hover:bg-[#16a34a] hover:text-white">MARK DELIVERED</button>
            <button onClick={() => bulkMark('cancelled')} className="px-3 py-1.5 rounded bg-red-500 text-white text-xs font-bold hover:bg-red-400">CANCEL</button>
            <button onClick={() => setSelected(new Set())} className="px-3 py-1.5 rounded border border-gray-500 text-xs font-bold hover:bg-gray-800">CLEAR</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-center py-10 text-gray-500">Loading...</div>
        : filtered.length === 0 ? <div className="text-center py-16 border-2 border-dashed rounded text-gray-500">No orders {statusFilter !== 'all' ? `with status "${statusFilter}"` : 'yet'}.</div>
        : (
          <div className="border rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="w-10 p-3">
                    <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={selectAll} className="accent-[#22c55e] w-4 h-4" />
                  </th>
                  <th className="text-left p-3">Order</th>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-right p-3">Items</th>
                  <th className="text-right p-3">Total</th>
                  <th className="text-center p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const status = (o.status || 'placed').toLowerCase();
                  const checked = selected.has(o.order_id);
                  return (
                    <tr key={o.order_id} className={`border-b hover:bg-gray-50 ${checked ? 'bg-green-50/60' : ''}`}>
                      <td className="p-3">
                        <input type="checkbox" checked={checked} onChange={() => toggleSelect(o.order_id)} className="accent-[#22c55e] w-4 h-4" />
                      </td>
                      <td className="p-3">
                        <button onClick={() => setDetailsOrder(o)} className="font-mono text-xs text-[#22c55e] hover:underline">
                          {o.order_id.slice(0, 8)}
                        </button>
                      </td>
                      <td className="p-3">
                        <button onClick={() => setDetailsOrder(o)} className="text-left">
                          <div className="font-semibold text-xs">{o.address?.name || o.user_id}</div>
                          <div className="text-[11px] text-gray-500">{o.user_id}</div>
                        </button>
                      </td>
                      <td className="p-3 text-xs">{new Date(o.created_at).toLocaleString()}</td>
                      <td className="p-3 text-right">{o.items?.length || 0}</td>
                      <td className="p-3 text-right font-bold">{o.currency === 'EUR' ? '€' : '₹'}{Number(o.total).toFixed(2)}</td>
                      <td className="p-3">
                        <div className="flex flex-col items-center gap-1.5">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>
                          <select
                            value={status}
                            onChange={(e) => changeStatus(o.order_id, e.target.value)}
                            className="text-[11px] border rounded px-2 py-1 outline-none focus:border-[#22c55e] bg-white"
                          >
                            {[...STATUS_STEPS, 'cancelled'].map((s) => (
                              <option key={s} value={s}>{s.toUpperCase()}</option>
                            ))}
                          </select>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      <OrderDetailsModal order={detailsOrder} onClose={() => setDetailsOrder(null)} />
    </div>
  );
};

// -------- Reviews tab --------
const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminFetchReviews();
      setReviews(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleHide = async (r) => {
    await adminToggleHideReview(r.review_id);
    toast({ title: r.hidden ? 'Review unhidden' : 'Review hidden from shop' });
    load();
  };

  const remove = async (r) => {
    const ok = await confirmDialog({
      title: 'Delete this review?',
      description: 'The review will be removed permanently.',
      actionLabel: 'Delete',
      tone: 'destructive',
      testId: 'confirm-delete-review',
    });
    if (!ok) return;
    await adminDeleteReview(r.review_id);
    toast({ title: 'Review deleted' });
    load();
  };

  if (loading) return <div className="text-center py-10 text-gray-500">Loading reviews...</div>;
  if (reviews.length === 0) return <div className="text-center py-16 border-2 border-dashed rounded text-gray-500">No reviews yet.</div>;

  return (
    <div className="grid md:grid-cols-2 gap-4">
      {reviews.map((r) => (
        <div key={r.review_id} className={`border rounded p-4 bg-white ${r.hidden ? 'opacity-60 border-red-200' : ''}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-bold text-sm">{r.user_name} <span className="text-gray-400 font-normal">on product #{r.product_id}</span></div>
              <div className="text-xs text-gray-500">{r.rating}★ · {new Date(r.created_at).toLocaleDateString()}</div>
            </div>
            {r.hidden && <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded font-bold">HIDDEN</span>}
          </div>
          {r.title && <h4 className="font-semibold mt-2 text-sm">{r.title}</h4>}
          <p className="text-sm text-gray-700 mt-1">{r.comment}</p>
          {r.photo_base64 && <img src={r.photo_base64} alt="" className="mt-2 max-h-40 rounded" />}
          <div className="mt-3 flex gap-2">
            <button onClick={() => toggleHide(r)} className="text-xs font-bold text-gray-700 hover:text-[#22c55e] flex items-center gap-1">
              {r.hidden ? <><Eye size={13} /> UNHIDE</> : <><EyeOff size={13} /> HIDE</>}
            </button>
            <button onClick={() => remove(r)} className="text-xs font-bold text-gray-700 hover:text-red-600 flex items-center gap-1">
              <Trash2 size={13} /> DELETE
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// -------- Stats tab --------
const StatCard = ({ label, value, sub }) => (
  <div className="border rounded p-5 bg-white">
    <div className="text-[10px] font-bold tracking-widest text-gray-500">{label}</div>
    <div className="text-3xl font-black mt-1">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

const StatsTab = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { adminFetchStats().then(setStats).catch(() => {}); }, []);
  if (!stats) return <div className="text-center py-10 text-gray-500">Loading stats...</div>;

  const maxDaily = Math.max(...stats.daily.map((d) => d.total), 1);
  const lowStockCount = stats.low_stock || 0;
  const lowItems = stats.low_stock_items || [];

  return (
    <div>
      {/* Order alerts push toggle */}
      <div className="mb-6"><PushToggle /></div>

      {/* Low stock alert banner */}
      {lowStockCount > 0 && (
        <div className="relative mb-6 border-2 border-red-500 rounded-lg bg-red-50 overflow-hidden">
          <span className="absolute -top-1 -right-1 flex h-4 w-4">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-4 w-4 bg-red-600"></span>
          </span>
          <div className="p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center shrink-0 animate-[pulse_1.5s_ease-in-out_infinite]">
              <AlertTriangle className="text-white" size={20} />
            </div>
            <div className="flex-1">
              <div className="font-black text-red-700 tracking-wider text-sm">LOW STOCK ALERT · {lowStockCount} PRODUCT{lowStockCount > 1 ? 'S' : ''}</div>
              <p className="text-xs text-red-600 mt-0.5">These items have 10 or fewer units left — restock soon.</p>
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                {lowItems.slice(0, 12).map((it) => (
                  <div key={it.id} className="min-w-[130px] bg-white border border-red-200 rounded p-2 flex items-center gap-2">
                    <img src={it.image} alt="" className="w-10 h-12 object-cover rounded shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold line-clamp-1">{it.name}</div>
                      <div className="text-[10px] text-red-600 font-bold">{it.stock} left</div>
                    </div>
                  </div>
                ))}
                {lowItems.length > 12 && <span className="text-xs text-red-600 self-center px-2">+{lowItems.length - 12} more</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="TOTAL ORDERS" value={stats.total_orders} />
        <StatCard label="REVENUE (₹ INR)" value={`₹${Number(stats.revenue_inr).toFixed(0)}`} />
        <StatCard label="REVENUE (€ EUR)" value={`€${Number(stats.revenue_eur).toFixed(2)}`} />
        <StatCard label="PRODUCTS" value={stats.products_count} sub={`${stats.out_of_stock} out of stock`} />
        <StatCard label="REVIEWS" value={stats.reviews_count} sub={`${stats.hidden_reviews} hidden`} />
      </div>

      <div className="mt-6 border rounded p-5 bg-white">
        <h3 className="font-bold text-sm tracking-wider mb-4">DAILY SALES (last 14 days)</h3>
        {stats.daily.length === 0 ? (
          <p className="text-sm text-gray-500">No sales yet.</p>
        ) : (
          <div className="flex items-end gap-2 h-40">
            {stats.daily.map((d) => (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                <div title={`${d.day}: ${d.total}`}
                  className="w-full bg-[#22c55e] rounded-t"
                  style={{ height: `${(d.total / maxDaily) * 100}%`, minHeight: '4px' }} />
                <div className="text-[9px] text-gray-500 rotate-45 origin-left">{d.day.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Top coupons */}
      {(stats.top_coupons && stats.top_coupons.length > 0) && (
        <div className="mt-6 border rounded p-4" data-testid="top-coupons-block">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-black">TOP COUPONS</h3>
            <span className="text-[10px] uppercase tracking-widest text-gray-500">By orders</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-xs">
              <tr>
                <th className="text-left p-2 font-semibold">Code</th>
                <th className="text-right p-2 font-semibold">Orders</th>
                <th className="text-right p-2 font-semibold">Revenue ₹</th>
                <th className="text-right p-2 font-semibold">Revenue €</th>
                <th className="text-right p-2 font-semibold">Discount given</th>
              </tr>
            </thead>
            <tbody>
              {stats.top_coupons.map((c) => (
                <tr key={c.code} className="border-b" data-testid={`top-coupon-${c.code}`}>
                  <td className="p-2 font-mono font-bold">{c.code} <span className="text-[10px] text-gray-500 font-normal">({c.percent}%)</span></td>
                  <td className="p-2 text-right font-semibold">{c.orders}</td>
                  <td className="p-2 text-right">₹{Math.round(c.revenue_inr || 0)}</td>
                  <td className="p-2 text-right">€{(c.revenue_eur || 0).toFixed(2)}</td>
                  <td className="p-2 text-right text-green-600">
                    ₹{Math.round(c.discount_inr || 0)} · €{(c.discount_eur || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Delete Test Orders panel */}
      <TestOrderCleanupPanel />
    </div>
  );
};

const TestOrderCleanupPanel = () => {
  const [days, setDays] = useState(30);
  const [domain, setDomain] = useState('feronstore.com');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Invalidate preview whenever filters change so DELETE always matches what's shown
  const setDaysSafe = (n) => { setDays(n); setPreview(null); };
  const setDomainSafe = (s) => { setDomain(s); setPreview(null); };

  const runPreview = async () => {
    setBusy(true);
    try {
      const res = await adminCleanupTestOrders({ days, email_domain: domain, dry_run: true });
      setPreview(res);
    } catch (err) {
      toast({ title: 'Preview failed', description: err.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    if (!preview || !preview.would_delete) return;
    setConfirmOpen(false);
    setBusy(true);
    try {
      const res = await adminCleanupTestOrders({ days, email_domain: domain, dry_run: false });
      toast({ title: `${res.deleted} order${res.deleted !== 1 ? 's' : ''} deleted` });
      setPreview(null);
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 border rounded p-4" data-testid="test-cleanup-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-black">DELETE TEST ORDERS</h3>
          <p className="text-xs text-gray-500">Wipes only orders whose customer email ends with a domain you choose. Nothing else is touched.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Older than (days)</label>
          <Input type="number" min={0} value={days} onChange={(e) => setDaysSafe(Number(e.target.value) || 0)} className="h-9 w-28" data-testid="cleanup-days" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Test email domain</label>
          <Input value={domain} onChange={(e) => setDomainSafe(e.target.value.trim())} className="h-9 w-60" data-testid="cleanup-domain" />
        </div>
        <Button variant="outline" onClick={runPreview} disabled={busy} className="h-9 font-bold text-xs" data-testid="cleanup-preview-btn">
          {busy ? '…' : 'PREVIEW'}
        </Button>
        <Button onClick={() => setConfirmOpen(true)} disabled={busy || !preview || !preview.would_delete} className="h-9 bg-red-600 hover:bg-red-700 text-white font-bold text-xs" data-testid="cleanup-delete-btn">
          <Trash2 size={14} className="mr-1" /> DELETE {preview?.would_delete ? preview.would_delete : ''}
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="cleanup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {preview?.would_delete ?? 0} test order{preview?.would_delete === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes every order whose customer email ends with{' '}
              <span className="font-mono font-semibold">@{domain}</span> and is older than{' '}
              <span className="font-semibold">{days}</span> day{days === 1 ? '' : 's'}. Real customer orders on other domains are untouched. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cleanup-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="cleanup-confirm-confirm"
            >
              Delete {preview?.would_delete ?? 0}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {preview && (
        <div className="mt-4" data-testid="cleanup-preview-result">
          <p className="text-xs text-gray-600 mb-2">
            <span className="font-bold">{preview.would_delete}</span> order(s) match — showing up to 20 in preview.
          </p>
          {preview.sample?.length > 0 ? (
            <div className="border rounded overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-2 font-semibold">Order</th>
                    <th className="text-left p-2 font-semibold">Email</th>
                    <th className="text-right p-2 font-semibold">Total</th>
                    <th className="text-left p-2 font-semibold">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.sample.map((o) => (
                    <tr key={o.order_id} className="border-b" data-testid={`cleanup-row-${o.order_id.slice(0, 8)}`}>
                      <td className="p-2 font-mono">{o.order_id.slice(0, 8)}</td>
                      <td className="p-2">{o.customer_email}</td>
                      <td className="p-2 text-right">{Number(o.total).toFixed(2)}</td>
                      <td className="p-2 text-gray-500">{o.created_at ? new Date(o.created_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-gray-500">Nothing to delete for this filter.</p>
          )}
        </div>
      )}
    </div>
  );
};
const CouponsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // full coupon doc or 'new'
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminFetchCoupons();
      setItems(data.items || []);
    } catch (err) {
      toast({ title: 'Load coupons failed', description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleActive = async (c) => {
    try {
      await adminUpdateCoupon(c.code, { active: !c.active });
      toast({ title: `${c.code} ${!c.active ? 'enabled' : 'disabled'}` });
      load();
    } catch (err) {
      toast({ title: 'Update failed', description: err.response?.data?.detail || err.message });
    }
  };

  const remove = async (c) => {
    const ok = await confirmDialog({
      title: `Delete coupon ${c.code}?`,
      description: 'Customers will no longer be able to redeem this code.',
      actionLabel: 'Delete',
      tone: 'destructive',
      testId: 'confirm-delete-coupon',
    });
    if (!ok) return;
    try {
      await adminDeleteCoupon(c.code);
      toast({ title: `${c.code} deleted` });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.response?.data?.detail || err.message });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-black text-xl">COUPONS</h2>
          <p className="text-xs text-gray-500">Launch promos, expire deals or disable a code — no redeploy needed.</p>
        </div>
        <Button onClick={() => setEditing('new')} className="bg-[#22c55e] hover:bg-[#16a34a] font-bold tracking-wider" data-testid="new-coupon-btn">
          <Plus size={16} className="mr-1" /> NEW COUPON
        </Button>
      </div>

      {loading ? <div className="text-center py-10 text-gray-500">Loading...</div> : (
        <div className="border rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-semibold">Code</th>
                <th className="text-right p-3 font-semibold">Discount</th>
                <th className="text-right p-3 font-semibold">Min ₹</th>
                <th className="text-right p-3 font-semibold">Uses</th>
                <th className="text-left p-3 font-semibold">Expires</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="text-right p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-gray-500">No coupons yet — click NEW COUPON.</td></tr>
              )}
              {items.map((c) => (
                <tr key={c.code} className="border-b hover:bg-gray-50" data-testid={`coupon-row-${c.code}`}>
                  <td className="p-3 font-mono font-bold">{c.code}</td>
                  <td className="p-3 text-right font-semibold">{c.percent}%</td>
                  <td className="p-3 text-right text-gray-500">{c.min_subtotal_inr ? `₹${c.min_subtotal_inr}` : '—'}</td>
                  <td className="p-3 text-right text-gray-500">{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                  <td className="p-3 text-xs text-gray-500">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : 'Never'}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${c.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                      {c.active ? 'ACTIVE' : 'DISABLED'}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => toggleActive(c)} className="text-gray-600 hover:text-[#22c55e] p-1" title={c.active ? 'Disable' : 'Enable'} data-testid={`coupon-toggle-${c.code}`}>
                      {c.active ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button onClick={() => setEditing(c)} className="text-gray-600 hover:text-[#22c55e] p-1 ml-1" data-testid={`coupon-edit-${c.code}`}><Edit2 size={16} /></button>
                    <button onClick={() => remove(c)} className="text-gray-600 hover:text-red-600 p-1 ml-1" data-testid={`coupon-delete-${c.code}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CouponEditor
          coupon={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          busy={busy}
          setBusy={setBusy}
        />
      )}
    </div>
  );
};

const CouponEditor = ({ coupon, onClose, onSaved, busy, setBusy }) => {
  const isNew = !coupon;
  const [form, setForm] = useState({
    code: coupon?.code || '',
    percent: coupon?.percent ?? 10,
    active: coupon?.active ?? true,
    min_subtotal_inr: coupon?.min_subtotal_inr ?? 0,
    max_uses: coupon?.max_uses ?? '',
    expires_at: coupon?.expires_at ? coupon.expires_at.slice(0, 10) : '',
    currency: coupon?.currency || '',
    description: coupon?.description || '',
  });
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.code.trim()) { toast({ title: 'Code is required' }); return; }
    setBusy(true);
    try {
      const payload = {
        code: form.code.toUpperCase().trim(),
        percent: Number(form.percent) || 0,
        active: !!form.active,
        min_subtotal_inr: Number(form.min_subtotal_inr) || 0,
        max_uses: form.max_uses === '' || form.max_uses === null ? null : Number(form.max_uses),
        expires_at: form.expires_at ? new Date(form.expires_at + 'T23:59:59Z').toISOString() : null,
        currency: form.currency || null,
        description: form.description || '',
      };
      if (isNew) {
        await adminCreateCoupon(payload);
        toast({ title: `${payload.code} created` });
      } else {
        const { code, ...patch } = payload;
        await adminUpdateCoupon(coupon.code, patch);
        toast({ title: `${coupon.code} updated` });
      }
      onSaved();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="coupon-editor">
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="font-black text-lg">{isNew ? 'NEW COUPON' : `EDIT ${coupon.code}`}</h3>
          <button onClick={onClose}><X /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-bold text-gray-700">CODE</label>
            <Input value={form.code} onChange={(e) => setField('code', e.target.value.toUpperCase())} disabled={!isNew} placeholder="SUMMER25" data-testid="coupon-code-input" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700">DISCOUNT %</label>
            <Input type="number" min={0} max={100} value={form.percent} onChange={(e) => setField('percent', e.target.value)} data-testid="coupon-percent-input" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700">MIN SUBTOTAL (₹)</label>
            <Input type="number" min={0} value={form.min_subtotal_inr} onChange={(e) => setField('min_subtotal_inr', e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700">MAX USES (blank = unlimited)</label>
            <Input type="number" min={0} value={form.max_uses} onChange={(e) => setField('max_uses', e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700">EXPIRES ON</label>
            <Input type="date" value={form.expires_at} onChange={(e) => setField('expires_at', e.target.value)} data-testid="coupon-expires-input" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700">CURRENCY LIMIT</label>
            <select value={form.currency} onChange={(e) => setField('currency', e.target.value)} className="w-full border rounded px-2 py-2 text-sm">
              <option value="">Any</option>
              <option value="INR">INR only</option>
              <option value="EUR">EUR only</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-bold text-gray-700">DESCRIPTION</label>
            <Input value={form.description} onChange={(e) => setField('description', e.target.value)} placeholder="Summer sale · 25% off everything" />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setField('active', e.target.checked)} className="accent-[#22c55e]" data-testid="coupon-active-toggle" />
            <span className="font-semibold">Active</span>
          </label>
        </div>
        <div className="border-t p-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>CANCEL</Button>
          <Button onClick={save} disabled={busy} className="bg-[#22c55e] hover:bg-[#16a34a]" data-testid="coupon-save-btn">
            {busy ? '...' : <><Save size={16} className="mr-1" /> SAVE</>}
          </Button>
        </div>
      </div>
    </div>
  );
};


const TaxonomiesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState('all');
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ kind: 'type', label: '', slug: '', image: '', gender_scope: 'all', sort_order: 0 });
  const [creating, setCreating] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [dragId, setDragId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminFetchTaxonomies();
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filterKind === 'all' ? items : items.filter((i) => i.kind === filterKind);
  const canReorder = filterKind !== 'all' && !editingId;

  const onDragStart = (e, id) => {
    if (!canReorder) return;
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox needs some data to enable drag
    e.dataTransfer.setData('text/plain', id);
  };
  const onDragOver = (e) => {
    if (!canReorder || !dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = async (e, targetId) => {
    if (!canReorder || !dragId || dragId === targetId) { setDragId(null); return; }
    e.preventDefault();
    // Reorder only within the current kind
    const currentKindItems = items.filter((i) => i.kind === filterKind).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const fromIdx = currentKindItems.findIndex((i) => i.id === dragId);
    const toIdx = currentKindItems.findIndex((i) => i.id === targetId);
    if (fromIdx === -1 || toIdx === -1) { setDragId(null); return; }
    const reordered = [...currentKindItems];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    // Optimistically update local state
    const newItems = items.map((it) => {
      if (it.kind !== filterKind) return it;
      const idx = reordered.findIndex((r) => r.id === it.id);
      return idx >= 0 ? { ...it, sort_order: idx } : it;
    });
    setItems(newItems);
    setDragId(null);
    setSavingOrder(true);
    try {
      await adminReorderTaxonomies(filterKind, reordered.map((r) => r.id));
      toast({ title: 'Order saved' });
    } catch (err) {
      toast({ title: 'Reorder failed', description: err.response?.data?.detail || err.message });
      load();
    } finally { setSavingOrder(false); }
  };
  const counts = {
    all: items.length,
    type: items.filter((i) => i.kind === 'type').length,
    collection: items.filter((i) => i.kind === 'collection').length,
    category: items.filter((i) => i.kind === 'category').length,
  };

  const createNew = async () => {
    if (!newForm.label.trim()) return toast({ title: 'Label required' });
    setCreating(true);
    try {
      const payload = { ...newForm, label: newForm.label.trim(), sort_order: +newForm.sort_order || 0 };
      if (!payload.image) delete payload.image;
      if (!payload.slug) delete payload.slug;
      if (payload.kind !== 'type') delete payload.gender_scope;
      await adminCreateTaxonomy(payload);
      toast({ title: `${newForm.kind} '${newForm.label}' created` });
      setNewForm({ kind: newForm.kind, label: '', slug: '', image: '', gender_scope: 'all', sort_order: 0 });
      setShowNew(false);
      load();
    } catch (err) {
      toast({ title: 'Create failed', description: err.response?.data?.detail || err.message });
    } finally { setCreating(false); }
  };

  const uploadImage = async (file, target) => {
    setUploadingImg(true);
    try {
      const { url } = await adminUploadFile(file);
      if (target === 'new') setNewForm((f) => ({ ...f, image: url }));
      else setEditDraft((d) => ({ ...d, image: url }));
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message });
    } finally { setUploadingImg(false); }
  };

  const startEdit = (it) => { setEditingId(it.id); setEditDraft({ label: it.label, slug: it.slug, image: it.image || '', gender_scope: it.gender_scope || 'all', sort_order: it.sort_order || 0, is_active: it.is_active }); };
  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };
  const saveEdit = async (id) => {
    try {
      await adminUpdateTaxonomy(id, editDraft);
      toast({ title: 'Saved' });
      cancelEdit();
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message });
    }
  };

  const remove = async (it) => {
    const ok = await confirmDialog({
      title: `Delete ${it.kind} '${it.label}'?`,
      description: 'If products are still tagged with it, they will keep the raw label but this entry will no longer appear in menus or filters.',
      actionLabel: 'Delete',
      tone: 'destructive',
      testId: 'confirm-delete-taxonomy',
    });
    if (!ok) return;
    try {
      await adminDeleteTaxonomy(it.id, false);
      toast({ title: `${it.label} deleted` });
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'IN_USE') {
        const force = await confirmDialog({
          title: `${detail.usage_count} product(s) still use '${it.label}'`,
          description: 'Delete anyway? Products will keep their raw text value but the entry will disappear from admin dropdowns and storefront tiles/menus.',
          actionLabel: 'Force delete',
          tone: 'destructive',
          testId: 'confirm-force-delete-taxonomy',
        });
        if (!force) return;
        try {
          await adminDeleteTaxonomy(it.id, true);
          toast({ title: `${it.label} force-deleted` });
          load();
        } catch (err2) {
          toast({ title: 'Force delete failed', description: err2.response?.data?.detail || err2.message });
        }
      } else {
        toast({ title: 'Delete failed', description: typeof detail === 'string' ? detail : err.message });
      }
    }
  };

  const kindBadge = (k) => (
    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${k === 'type' ? 'bg-emerald-100 text-emerald-700' : k === 'collection' ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'}`}>{k}</span>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black">Taxonomies</h2>
          <p className="text-xs text-gray-500">Types, collections and homepage category tiles. Changes reflect on the storefront immediately after refresh.</p>
        </div>
        <Button onClick={() => setShowNew((s) => !s)} className="bg-black hover:bg-neutral-800 text-white" data-testid="taxonomy-new-btn">
          <Plus size={16} className="mr-1" /> {showNew ? 'Close' : 'New entry'}
        </Button>
      </div>

      {showNew && (
        <div className="bg-white border rounded p-4 mb-4 grid md:grid-cols-2 gap-3" data-testid="taxonomy-new-form">
          <div>
            <label className="text-xs font-bold text-gray-700">KIND</label>
            <select value={newForm.kind} onChange={(e) => setNewForm({ ...newForm, kind: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" data-testid="taxonomy-new-kind">
              <option value="type">Type (product type — e.g. Hoodies)</option>
              <option value="collection">Collection (brand collab — e.g. Marvel)</option>
              <option value="category">Category (homepage tile)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700">LABEL *</label>
            <Input value={newForm.label} onChange={(e) => setNewForm({ ...newForm, label: e.target.value })} data-testid="taxonomy-new-label" placeholder="e.g. Marvel" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-700">SLUG (auto if blank)</label>
            <Input value={newForm.slug} onChange={(e) => setNewForm({ ...newForm, slug: e.target.value })} placeholder="marvel" />
          </div>
          {newForm.kind === 'type' && (
            <div>
              <label className="text-xs font-bold text-gray-700">GENDER SCOPE</label>
              <select value={newForm.gender_scope} onChange={(e) => setNewForm({ ...newForm, gender_scope: e.target.value })} className="w-full border rounded px-3 py-2 text-sm">
                <option value="all">All</option>
                <option value="men">Men</option>
                <option value="women">Women</option>
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-bold text-gray-700">SORT ORDER</label>
            <Input type="number" value={newForm.sort_order} onChange={(e) => setNewForm({ ...newForm, sort_order: e.target.value })} />
          </div>
          {(newForm.kind === 'category' || newForm.kind === 'collection') && (
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-gray-700">TILE IMAGE</label>
              <div className="flex items-center gap-2">
                <Input value={newForm.image} onChange={(e) => setNewForm({ ...newForm, image: e.target.value })} placeholder="https://... or upload" />
                <label className="cursor-pointer border rounded px-3 py-2 text-xs font-bold hover:bg-gray-50 whitespace-nowrap flex items-center gap-1">
                  <ImagePlus size={14} />{uploadingImg ? 'Uploading…' : 'Upload'}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], 'new')} />
                </label>
              </div>
              {newForm.image && <img src={newForm.image} alt="" className="mt-2 h-16 w-16 object-cover rounded border" />}
            </div>
          )}
          <div className="md:col-span-2 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button disabled={creating} onClick={createNew} className="bg-[#22c55e] hover:bg-[#16a34a]" data-testid="taxonomy-new-save">
              {creating ? '...' : 'Create'}
            </Button>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-3 flex-wrap">
        {[
          { k: 'all', label: `All (${counts.all})` },
          { k: 'type', label: `Types (${counts.type})` },
          { k: 'collection', label: `Collections (${counts.collection})` },
          { k: 'category', label: `Categories (${counts.category})` },
        ].map((f) => (
          <button key={f.k} onClick={() => setFilterKind(f.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${filterKind === f.k ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200 hover:border-black'}`}
            data-testid={`taxonomy-filter-${f.k}`}>
            {f.label}
          </button>
        ))}
      </div>

      {canReorder && (
        <div className="text-xs text-gray-500 mb-2 flex items-center gap-1.5" data-testid="taxonomy-reorder-hint">
          <GripVertical size={12} className="text-gray-400" />
          Drag rows to reorder {filterKind === 'category' ? 'homepage tiles' : filterKind === 'collection' ? 'brand collabs' : 'type filters'}
          {savingOrder && <span className="text-[#22c55e] font-semibold">· saving…</span>}
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded text-gray-500">No entries. Click <b>New entry</b> to add one.</div>
      ) : (
        <div className="bg-white border rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                <th className="p-3 w-8"></th>
                <th className="p-3">Kind</th>
                <th className="p-3">Label</th>
                <th className="p-3">Slug</th>
                <th className="p-3">Image</th>
                <th className="p-3">Order</th>
                <th className="p-3">Active</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(canReorder ? [...filtered].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)) : filtered).map((it) => {
                const isEditing = editingId === it.id;
                const isDragging = dragId === it.id;
                return (
                  <tr
                    key={it.id}
                    className={`border-t hover:bg-gray-50 ${isDragging ? 'opacity-40' : ''} ${canReorder ? 'cursor-move' : ''}`}
                    data-testid={`taxonomy-row-${it.slug}`}
                    draggable={canReorder}
                    onDragStart={(e) => onDragStart(e, it.id)}
                    onDragOver={onDragOver}
                    onDrop={(e) => onDrop(e, it.id)}
                    onDragEnd={() => setDragId(null)}
                  >
                    <td className="p-3 text-gray-300">
                      {canReorder ? <GripVertical size={14} data-testid={`taxonomy-drag-${it.slug}`} /> : null}
                    </td>
                    <td className="p-3">{kindBadge(it.kind)}</td>
                    <td className="p-3">
                      {isEditing ? (
                        <Input value={editDraft.label} onChange={(e) => setEditDraft({ ...editDraft, label: e.target.value })} className="h-8" />
                      ) : (
                        <span className="font-semibold">{it.label}</span>
                      )}
                    </td>
                    <td className="p-3 text-gray-500 font-mono text-xs">
                      {isEditing ? (
                        <Input value={editDraft.slug} onChange={(e) => setEditDraft({ ...editDraft, slug: e.target.value })} className="h-8" />
                      ) : it.slug}
                    </td>
                    <td className="p-3">
                      {isEditing && (it.kind === 'category' || it.kind === 'collection') ? (
                        <div className="flex items-center gap-2">
                          <Input value={editDraft.image || ''} onChange={(e) => setEditDraft({ ...editDraft, image: e.target.value })} className="h-8 w-40" />
                          <label className="cursor-pointer border rounded px-2 py-1 text-[10px] font-bold hover:bg-gray-50">
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], 'edit')} />
                            {uploadingImg ? '...' : 'Upload'}
                          </label>
                        </div>
                      ) : it.image ? <img src={it.image} alt="" className="h-10 w-10 object-cover rounded" /> : <span className="text-xs text-gray-400">—</span>}
                    </td>
                    <td className="p-3">
                      {isEditing ? (
                        <Input type="number" value={editDraft.sort_order} onChange={(e) => setEditDraft({ ...editDraft, sort_order: +e.target.value })} className="h-8 w-20" />
                      ) : it.sort_order}
                    </td>
                    <td className="p-3">
                      {isEditing ? (
                        <input type="checkbox" checked={!!editDraft.is_active} onChange={(e) => setEditDraft({ ...editDraft, is_active: e.target.checked })} className="accent-[#22c55e] h-4 w-4" />
                      ) : (
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold ${it.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{it.is_active ? 'ACTIVE' : 'HIDDEN'}</span>
                      )}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {isEditing ? (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => saveEdit(it.id)} className="p-1.5 rounded hover:bg-green-100 text-green-700" title="Save" data-testid={`taxonomy-save-${it.slug}`}><Save size={14} /></button>
                          <button onClick={cancelEdit} className="p-1.5 rounded hover:bg-gray-100 text-gray-500" title="Cancel"><X size={14} /></button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => startEdit(it)} className="p-1.5 rounded hover:bg-blue-100 text-blue-700" title="Edit" data-testid={`taxonomy-edit-${it.slug}`}><Edit2 size={14} /></button>
                          <button onClick={() => remove(it)} className="p-1.5 rounded hover:bg-red-100 text-red-700" title="Delete" data-testid={`taxonomy-delete-${it.slug}`}><Trash2 size={14} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MessagesTab = ({ onRead }) => {
  const [convos, setConvos] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = React.useRef(null);

  const loadConvos = React.useCallback(async () => {
    try { const d = await adminChatConversations(); setConvos(d.conversations || []); } catch (e) { console.warn('[admin chat] load conversations failed:', e?.message || e); }
  }, []);

  const loadThread = React.useCallback(async (uid) => {
    if (!uid) return;
    try {
      const d = await adminChatThread(uid);
      setThread(d.messages || []);
      if (onRead) onRead();
      requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; });
    } catch (e) { console.warn('[admin chat] load thread failed:', e?.message || e); }
  }, [onRead]);

  useEffect(() => { loadConvos(); const id = setInterval(loadConvos, 8000); return () => clearInterval(id); }, [loadConvos]);

  useEffect(() => {
    if (!activeUser) return;
    loadThread(activeUser.user_id);
    const id = setInterval(() => loadThread(activeUser.user_id), 5000);
    return () => clearInterval(id);
  }, [activeUser, loadThread]);

  const openConvo = (c) => {
    setActiveUser(c);
    setConvos((list) => list.map((x) => x.user_id === c.user_id ? { ...x, unread: 0 } : x));
  };

  const sendReply = async (e, presetText) => {
    if (e && e.preventDefault) e.preventDefault();
    const t = (presetText != null ? presetText : reply).trim();
    if (!t || sending || !activeUser) return;
    setSending(true);
    if (presetText == null) setReply('');
    try { await adminChatReply(activeUser.user_id, t); await loadThread(activeUser.user_id); loadConvos(); }
    catch { if (presetText == null) setReply(t); }
    finally { setSending(false); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
  const money = (amt, cur) => `${cur === 'EUR' ? '€' : '₹'}${Number(amt || 0).toFixed(2)}`;

  const CANNED = [
    { key: 'shipping', label: 'Delivery', text: "Great news — your order is on its way! Made in India, we dispatch within 24-48 hours and deliver in 6-7 days across India (7-12 business days for international). You'll get a tracking link by email as soon as it's out for delivery. 📦" },
    { key: 'sizing', label: 'Sizing', text: "For the best fit, please check the measurements on the product page. Our tees have an oversized fit — if you're between sizes we'd suggest sizing down. Tell us your usual size and we'll guide you! 📏" },
    { key: 'returns', label: 'Returns', text: "We offer easy 7-day exchanges on unworn items with tags intact. We don't process refunds, but you're welcome to exchange for a different size or design — just reply here and we'll set it up! 🔁" },
  ];

  return (
    <div>
      <h2 className="text-xl font-black mb-4">Customer Messages</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Conversations list */}
        <div className="bg-white rounded-xl border overflow-hidden md:h-[600px] md:overflow-y-auto">
          {convos.length === 0 && <div className="p-6 text-center text-sm text-gray-400">No messages yet. Customer chats will appear here.</div>}
          {convos.map((c) => (
            <button
              key={c.user_id}
              onClick={() => openConvo(c)}
              data-testid={`admin-convo-${c.user_id}`}
              className={`w-full text-left px-4 py-3 border-b hover:bg-neutral-50 transition ${activeUser?.user_id === c.user_id ? 'bg-neutral-100' : ''}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-sm truncate">{c.user_name || c.user_email || 'Customer'}</span>
                {c.unread > 0 && <span className="shrink-0 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">{c.unread}</span>}
              </div>
              {c.latest_order ? (
                <div className="text-[10px] text-gray-600 mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <span className="font-mono font-semibold text-gray-700">#{c.latest_order.short_id}</span>
                  <span className={`px-1.5 py-0.5 rounded-full font-semibold ${c.latest_order.status === 'delivered' ? 'bg-green-100 text-green-700' : c.latest_order.status === 'shipped' ? 'bg-blue-100 text-blue-700' : c.latest_order.status === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>{(c.latest_order.status || 'placed').toUpperCase()}</span>
                  <span className="text-gray-500">{money(c.latest_order.total, c.latest_order.currency)}</span>
                </div>
              ) : (
                <div className="text-[10px] text-gray-400 mt-0.5">No orders yet</div>
              )}
              <div className="text-xs text-gray-500 truncate mt-0.5">{c.last_sender === 'admin' ? 'You: ' : ''}{c.last_text}</div>
              <div className="text-[10px] text-gray-400 mt-0.5">{fmt(c.last_at)}</div>
            </button>
          ))}
        </div>

        {/* Thread */}
        <div className="md:col-span-2 bg-white rounded-xl border flex flex-col md:h-[600px]">
          {!activeUser ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400 p-8">Select a conversation to read and reply</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-bold text-sm truncate">{activeUser.user_name || 'Customer'}</div>
                  <div className="text-xs text-gray-500 truncate">{activeUser.user_email}</div>
                </div>
                {activeUser.latest_order && (
                  <div className="text-right shrink-0">
                    <div className="text-[11px] font-mono font-semibold text-gray-700">#{activeUser.latest_order.short_id}</div>
                    <div className="text-[10px] text-gray-500">{(activeUser.latest_order.status || 'placed').toUpperCase()} · {money(activeUser.latest_order.total, activeUser.latest_order.currency)}</div>
                  </div>
                )}
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-neutral-50 min-h-[320px]">
                {thread.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${m.sender === 'admin' ? 'bg-[#22c55e] text-black rounded-br-sm' : 'bg-white border text-gray-800 rounded-bl-sm'}`}>
                      <div className="whitespace-pre-wrap break-words">{m.text}</div>
                      <div className={`text-[10px] mt-1 ${m.sender === 'admin' ? 'text-black/60' : 'text-gray-400'}`}>{fmt(m.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t">
                <div className="flex gap-1.5 px-2.5 pt-2.5 flex-wrap">
                  {CANNED.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => sendReply(null, c.text)}
                      disabled={sending}
                      data-testid={`canned-${c.key}`}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#22c55e] text-[#16a34a] hover:bg-[#22c55e] hover:text-black disabled:opacity-40 transition"
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <form onSubmit={sendReply} className="p-2.5 flex items-center gap-2">
                  <input value={reply} onChange={(e) => setReply(e.target.value)} data-testid="admin-reply-input" placeholder="Type a reply..." className="flex-1 bg-neutral-100 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#22c55e]" />
                  <button type="submit" disabled={sending || !reply.trim()} data-testid="admin-reply-send" className="h-10 px-5 rounded-full bg-[#22c55e] hover:bg-[#1ea34d] disabled:opacity-40 text-black font-bold text-sm">Send</button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const TABS = [
  { key: 'stats', label: 'Overview', icon: BarChart3 },
  { key: 'products', label: 'Products', icon: Package },
  { key: 'taxonomies', label: 'Taxonomies', icon: Layers },
  { key: 'orders', label: 'Orders', icon: ShoppingBag },
  { key: 'coupons', label: 'Coupons', icon: Tag },
  { key: 'reviews', label: 'Reviews', icon: MessageSquare },
  { key: 'messages', label: 'Messages', icon: MessageCircle },
];

const AdminDashboard = () => {
  const [tab, setTab] = useState('stats');
  const [chatUnread, setChatUnread] = useState(0);
  const navigate = useNavigate();

  const refreshChatUnread = React.useCallback(async () => {
    try { const d = await adminChatUnreadTotal(); setChatUnread(d.unread || 0); } catch (e) { console.warn('[admin chat] unread total failed:', e?.message || e); }
  }, []);

  useEffect(() => {
    let active = true;
    const tick = async () => {
      try { const d = await adminChatUnreadTotal(); if (active) setChatUnread(d.unread || 0); } catch (e) { console.warn('[admin chat] unread poll failed:', e?.message || e); }
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { active = false; clearInterval(id); };
  }, [tab]);

  const doLogout = async () => {
    try { await adminLogout(); } catch (e) { console.warn('[admin] logout request failed:', e?.message || e); }
    setAdminToken(null);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-black text-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Feron" className="h-10 w-auto object-contain" />
            <span className="text-[11px] tracking-[0.3em] text-[#22c55e] font-bold">OWNER PORTAL</span>
          </div>
          <button onClick={doLogout} className="text-xs font-bold text-gray-300 hover:text-[#22c55e] flex items-center gap-1">
            <LogOut size={14} /> LOGOUT
          </button>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex gap-1 overflow-x-auto">
          {TABS.map((tb) => {
            const Icon = tb.icon;
            const active = tab === tb.key;
            return (
              <button key={tb.key} onClick={() => setTab(tb.key)}
                className={`relative flex items-center gap-1 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition ${active ? 'border-[#22c55e] text-[#22c55e]' : 'border-transparent text-gray-400 hover:text-white'}`}>
                <Icon size={16} /> {tb.label}
                {tb.key === 'messages' && chatUnread > 0 && (
                  <span data-testid="admin-messages-badge" className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">{chatUnread > 9 ? '9+' : chatUnread}</span>
                )}
              </button>
            );
          })}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {tab === 'stats' && <StatsTab />}
        {tab === 'products' && <ProductsTab />}
        {tab === 'taxonomies' && <TaxonomiesTab />}
        {tab === 'orders' && <OrdersTab />}
        {tab === 'coupons' && <CouponsTab />}
        {tab === 'reviews' && <ReviewsTab />}
        {tab === 'messages' && <MessagesTab onRead={refreshChatUnread} />}
      </main>
    </div>
  );
};

// -------- Wrapper --------
const AdminPage = () => {
  const [token, setToken] = useState(getAdminToken());
  if (!token) return (<><LoginGate onLogin={setToken} /><InstallAppHint /><ConfirmDialogHost /></>);
  return (<><AdminDashboard /><InstallAppHint /><ConfirmDialogHost /></>);
};

export default AdminPage;
